import type { components } from '@fluencelabs/vodopad-admin-api'
import { useQueryClient } from '@tanstack/react-query'
import { App, Form, Input, InputNumber, Modal, Select, Switch } from 'antd'
import { useEffect, useState } from 'react'

import client from '@/api/client'
import { mutateNoBody } from '@/api/mutateNoBody'
import { PAYMENT_METHOD_KINDS } from '@/constants/payments'
import { invalidateAdminCaches } from '@/utils/cacheInvalidation'
import { centsToDollars, dollarsToCents } from '@/utils/money'

type PaymentMethodDto = components['schemas']['PaymentMethodDto']

interface Props {
  open: boolean
  method: PaymentMethodDto | undefined
  onClose: () => void
}

export function PaymentMethodModal({ open, method, onClose }: Props) {
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const isEdit = Boolean(method)

  useEffect(() => {
    if (!open) return
    if (method) {
      form.setFieldsValue({
        provider: method.provider,
        methodCode: method.methodCode,
        displayName: method.displayName,
        kind: method.kind,
        enabled: method.enabled,
        minAmount: centsToDollars(method.minAmountCents),
        maxAmount: centsToDollars(method.maxAmountCents),
        estFeeBps: method.estFeeBps,
        estFeeFixed: centsToDollars(method.estFeeFixedCents),
        feePaidByUser: method.feePaidByUser,
        sortOrder: method.sortOrder,
      })
    } else {
      form.resetFields()
      form.setFieldsValue({ enabled: true, feePaidByUser: true })
    }
  }, [method, open, form])

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      const body: components['schemas']['AdminPaymentMethodRequest'] = {
        provider: values.provider,
        methodCode: values.methodCode,
        displayName: values.displayName,
        kind: values.kind,
        enabled: values.enabled,
        minAmountCents: dollarsToCents(values.minAmount),
        maxAmountCents:
          values.maxAmount === null || values.maxAmount === undefined
            ? null
            : dollarsToCents(values.maxAmount),
        estFeeBps: values.estFeeBps ?? 0,
        estFeeFixedCents: dollarsToCents(values.estFeeFixed),
        feePaidByUser: values.feePaidByUser,
        sortOrder: values.sortOrder ?? 0,
      }

      await mutateNoBody(
        (parseAs) =>
          client.POST('/v2/admin/payment-methods', { body, parseAs }),
        'Failed to save payment method',
      )
      void message.success(
        isEdit ? 'Payment method updated' : 'Payment method created',
      )
      onClose()
      invalidateAdminCaches(queryClient)
    } catch (error) {
      if (error instanceof Error) {
        console.error(error)
        void message.error(error.message)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={
        method
          ? `Edit payment method · ${method.provider} · ${method.methodCode}`
          : 'Add payment method'
      }
      open={open}
      onOk={() => void handleSubmit()}
      okText={isEdit ? 'Save payment method' : 'Add payment method'}
      onCancel={onClose}
      confirmLoading={loading}
      destroyOnHidden
      width={560}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="provider"
          label="Provider"
          rules={[{ required: true }]}
        >
          <Input placeholder="copperx" disabled={isEdit} />
        </Form.Item>
        <Form.Item
          name="methodCode"
          label="Method code"
          rules={[{ required: true }]}
        >
          <Input placeholder="card" disabled={isEdit} />
        </Form.Item>
        <Form.Item
          name="displayName"
          label="Display name"
          rules={[{ required: true }]}
        >
          <Input placeholder="Credit card" />
        </Form.Item>
        <Form.Item name="kind" label="Kind" rules={[{ required: true }]}>
          <Select
            placeholder="Select kind"
            options={PAYMENT_METHOD_KINDS.map((k) => ({ label: k, value: k }))}
          />
        </Form.Item>
        <Form.Item name="minAmount" label="Min amount ($)">
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="maxAmount"
          label="Max amount ($)"
          tooltip="Leave empty for no cap"
        >
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="estFeeBps"
          label="Est. fee (basis points)"
          tooltip="100 bps = 1%"
        >
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="estFeeFixed" label="Est. fixed fee ($)">
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="sortOrder" label="Sort order">
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="feePaidByUser"
          label="Fee paid by user"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item name="enabled" label="Enabled" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
